import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, ChevronLeft, Users } from 'lucide-react';
import { useActiveFederation } from '../context/ActiveFederationContext';
import { GUEST_ROLE_LABEL, type GuestRole } from '../types/federation';

/**
 * Gates the UI when the caller is acting as a Guest inside another tenant's
 * workspace via a federation.
 *
 * Two responsibilities:
 *   1. Show a persistent banner at the top of every page indicating
 *      "Acting as Guest Architect inside [Client]'s workspace".
 *   2. Block routes that fall outside the federation scope. Today the
 *      scope is path-prefix-based (Guest Auditors only see /evidence,
 *      Guest Architects can access most of the workspace, etc.). When
 *      backend enforces scope at the endpoint level, this gate becomes
 *      defence in depth.
 *
 * If there is no active federation (the caller is operating in their own
 * tenant), the gate is a pass-through — it renders children with no banner.
 */

const PATHS_FORBIDDEN_FOR_GUEST_AUDITOR = [
  '/tenants/settings',     // org-level config is for the owner only
  '/federations/new',      // can't initiate new federations as a guest
  '/user',                 // user management of host tenant is off-limits
  '/permission',
];

const PATHS_FORBIDDEN_FOR_GUEST_PRACTITIONER = [
  '/tenants/settings',
  '/permission',
];

function isForbiddenForRole(path: string, role: GuestRole): boolean {
  const forbidden =
    role === 'guest_auditor'
      ? PATHS_FORBIDDEN_FOR_GUEST_AUDITOR
      : role === 'guest_practitioner'
      ? PATHS_FORBIDDEN_FOR_GUEST_PRACTITIONER
      : []; // guest_architect has the broadest access
  return forbidden.some((p) => path === p || path.startsWith(`${p}/`));
}

const FederationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { active, guestRole, isLoading } = useActiveFederation();
  const location = useLocation();
  const navigate = useNavigate();

  // No active federation → pass-through.
  if (isLoading || !active || !guestRole) {
    return <>{children}</>;
  }

  const path = location.pathname;
  const blocked = isForbiddenForRole(path, guestRole);

  if (blocked) {
    return (
      <div className="min-h-[70vh] px-6 py-6">
        <GuestBanner federationName={active.toTenantName} role={guestRole} />
        <div className="mt-4 flex items-center justify-center px-6">
          <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <Shield className="h-7 w-7" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Out of federation scope</h1>
            <p className="mt-2 text-sm text-slate-600">
              You are acting as <strong>{GUEST_ROLE_LABEL[guestRole]}</strong> inside{' '}
              <strong>{active.toTenantName}</strong>'s workspace. This page is outside the
              scope granted by your federation.
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
            >
              <ChevronLeft className="h-4 w-4" /> Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <GuestBanner federationName={active.toTenantName} role={guestRole} />
      {children}
    </>
  );
};

const GuestBanner: React.FC<{ federationName: string; role: GuestRole }> = ({
  federationName,
  role,
}) => (
  <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-violet-200 bg-violet-50 px-6 py-2 text-xs">
    <Users className="h-3.5 w-3.5 text-violet-700" />
    <span className="font-semibold text-violet-900">
      Acting as {GUEST_ROLE_LABEL[role]} inside {federationName}
    </span>
    <span className="text-violet-700">
      · Your actions are scoped by the active federation. The host tenant retains all
      data.
    </span>
  </div>
);

export default FederationGate;
